import { Dayjs } from 'dayjs';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel,
  SelectChangeEvent,
  IconButton,
  Tooltip
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { useTranslation } from 'react-i18next';

// Local types to avoid class-transformer dependencies
export enum DayOfWeek {
  MONDAY = 'monday',
  TUESDAY = 'tuesday',
  WEDNESDAY = 'wednesday',
  THURSDAY = 'thursday',
  FRIDAY = 'friday',
  SATURDAY = 'saturday',
  SUNDAY = 'sunday',
}

export interface CasesByDayAndHourResponseDto {
  hour: number;
  count: number;
}

interface TrendsSectionProps {
  startDate?: Dayjs | null;
  endDate?: Dayjs | null;
  casesByDayOfWeek?: {
    monday: number;
    tuesday: number;
    wednesday: number;
    thursday: number;
    friday: number;
    saturday: number;
    sunday: number;
  };
  casesByHourPattern?: CasesByDayAndHourResponseDto[];
  selectedDayOfWeek?: DayOfWeek;
  onDayOfWeekChange?: (dayOfWeek: DayOfWeek) => void;
  onRefresh?: () => void;
}

// Map DayOfWeek enum to translation keys
const dayOfWeekToTranslationKey = (dayOfWeek: DayOfWeek): string => {
  const mapping = {
    [DayOfWeek.MONDAY]: 'dashboard.trends.weekdays.monday',
    [DayOfWeek.TUESDAY]: 'dashboard.trends.weekdays.tuesday',
    [DayOfWeek.WEDNESDAY]: 'dashboard.trends.weekdays.wednesday',
    [DayOfWeek.THURSDAY]: 'dashboard.trends.weekdays.thursday',
    [DayOfWeek.FRIDAY]: 'dashboard.trends.weekdays.friday',
    [DayOfWeek.SATURDAY]: 'dashboard.trends.weekdays.saturday',
    [DayOfWeek.SUNDAY]: 'dashboard.trends.weekdays.sunday',
  };
  return mapping[dayOfWeek];
};

// Get weekday options for the selector
const getWeekDayOptions = (t: (key: string) => string): Array<{ value: DayOfWeek; label: string }> => [
  { value: DayOfWeek.MONDAY, label: t('dashboard.trends.weekdays.monday') },
  { value: DayOfWeek.TUESDAY, label: t('dashboard.trends.weekdays.tuesday') },
  { value: DayOfWeek.WEDNESDAY, label: t('dashboard.trends.weekdays.wednesday') },
  { value: DayOfWeek.THURSDAY, label: t('dashboard.trends.weekdays.thursday') },
  { value: DayOfWeek.FRIDAY, label: t('dashboard.trends.weekdays.friday') },
  { value: DayOfWeek.SATURDAY, label: t('dashboard.trends.weekdays.saturday') },
  { value: DayOfWeek.SUNDAY, label: t('dashboard.trends.weekdays.sunday') },
];

// Transform hour pattern data for the chart
const getCasesByHour = (casesByHourPattern?: CasesByDayAndHourResponseDto[]) => {
  if (!casesByHourPattern) {
    // Return empty data for all 24 hours
    return Array.from({ length: 24 }, (_, index) => ({
      hour: index,
      value: 0
    }));
  }
  
  return casesByHourPattern.map(item => ({
    hour: item.hour,
    value: item.count
  }));
};

const getCasesByWeekDay = (t: (key: string) => string, casesByDayOfWeek?: TrendsSectionProps['casesByDayOfWeek']) => {
  return [
    { label: t('dashboard.trends.weekdaysShort.mon'), value: casesByDayOfWeek?.monday || 0 },
    { label: t('dashboard.trends.weekdaysShort.tue'), value: casesByDayOfWeek?.tuesday || 0 },
    { label: t('dashboard.trends.weekdaysShort.wed'), value: casesByDayOfWeek?.wednesday || 0 },
    { label: t('dashboard.trends.weekdaysShort.thu'), value: casesByDayOfWeek?.thursday || 0 },
    { label: t('dashboard.trends.weekdaysShort.fri'), value: casesByDayOfWeek?.friday || 0 },
    { label: t('dashboard.trends.weekdaysShort.sat'), value: casesByDayOfWeek?.saturday || 0 },
    { label: t('dashboard.trends.weekdaysShort.sun'), value: casesByDayOfWeek?.sunday || 0 }
  ];
};

export const TrendsSection = (props: TrendsSectionProps) => {
  const { t } = useTranslation();
  const { selectedDayOfWeek = DayOfWeek.MONDAY, onDayOfWeekChange, casesByHourPattern, onRefresh } = props;

  const handleWeekDayChange = (event: SelectChangeEvent) => {
    const newDayOfWeek = event.target.value as DayOfWeek;
    onDayOfWeekChange?.(newDayOfWeek);
  };

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          {t('dashboard.sections.activityTrends')}
        </Typography>
        {onRefresh && (
          <Tooltip title={t('common.refreshData')}>
            <IconButton
              onClick={onRefresh}
              size="small"
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': { backgroundColor: 'action.hover' }
              }}
            >
              <Refresh />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      
      {/* Filtro de día de la semana */}
      <Box sx={{ mb: 3, maxWidth: 300 }}>
        <FormControl fullWidth size="small">
          <InputLabel>{t('dashboard.trends.weekday')}</InputLabel>
          <Select
            value={selectedDayOfWeek}
            label={t('dashboard.trends.weekday')}
            onChange={handleWeekDayChange}
          >
            {getWeekDayOptions(t).map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Gráficos de Tendencias */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper elevation={1} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t('dashboard.charts.casesByHourPattern', { day: t(dayOfWeekToTranslationKey(selectedDayOfWeek)) })}
            </Typography>
            <LineChart
              dataset={getCasesByHour(casesByHourPattern)}
              xAxis={[{ scaleType: 'linear', dataKey: 'hour', min: 0, max: 23 }]}
              series={[{ dataKey: 'value', color: '#2e7d32' }]}
              width={400}
              height={300}
            />
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper elevation={1} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t('dashboard.charts.casesByWeekday')}
            </Typography>
            <BarChart
              dataset={getCasesByWeekDay(t, props.casesByDayOfWeek)}
              xAxis={[{ scaleType: 'band', dataKey: 'label' }]}
              series={[{ dataKey: 'value', color: '#9c27b0' }]}
              width={400}
              height={300}
            />
          </Paper>
        </Grid>
      </Grid>
    </Paper>
  );
}; 